<template>
  <el-dialog
    :visible="visible"
    width="440px"
    append-to-body
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :custom-class="`nd-modal nd-modal--${tone}`"
  >
    <div class="nd-modal__body">
      <div class="nd-modal__badge">
        <span :class="['nd-modal__ring', { 'nd-modal__ring--spin': loading }]"></span>
        <i :class="['fa', icon, 'nd-modal__icon', { 'nd-modal__icon--pulse': loading }]"></i>
      </div>
      <h4 class="nd-modal__title">{{ title }}</h4>
      <p class="nd-modal__text">{{ text }}</p>
      <p v-if="hint" class="nd-modal__hint"><i class="fa fa-lightbulb-o"></i> {{ hint }}</p>
    </div>

    <el-progress
      v-if="loading"
      class="nd-modal__progress"
      :percentage="progress"
      :stroke-width="6"
      :show-text="false"
      color="rgb(104, 48, 144)"
    />
    <p v-if="loading && progressText" class="nd-modal__progress-text">{{ progressText }}</p>

    <div v-if="!loading" slot="footer" class="nd-modal__footer">
      <el-button size="small" icon="el-icon-close" @click="$emit('close')">Close</el-button>
      <el-button
        v-if="showRefresh"
        type="primary"
        size="small"
        icon="el-icon-refresh"
        :loading="refreshing"
        @click="$emit('refresh')"
      >
        Refresh
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
/**
 * A pop-up for a screen that has nothing to show yet, with a refresh to
 * check again. It only reports what was clicked: the parent decides what
 * Close and Refresh do.
 */
export default {
  name: 'NoDataModal',
  props: {
    visible: { type: Boolean, default: false },
    refreshing: { type: Boolean, default: false },
    /**
     * info, warning or danger: the accent colour of the pop-up.
     */
    tone: { type: String, default: 'info' },
    showRefresh: { type: Boolean, default: true },
    /**
     * Shows the animated loading look with a progress bar (0 to 100) and no
     * buttons.
     */
    loading: { type: Boolean, default: false },
    progress: { type: Number, default: 0 },
    progressText: { type: String, default: '' },
    icon: { type: String, default: 'fa-database' },
    title: { type: String, default: 'No listener data yet' },
    text: {
      type: String,
      default:
        "Segmentation is turned on for this station, but there is no listener data to show. Segments, insights and charts appear here once the station's data has been loaded.",
    },
    hint: {
      type: String,
      default: 'If the data was loaded recently, refresh to check again.',
    },
  },
};
</script>

<style lang="scss">
/* The dialog is drawn outside the page, so these rules are not scoped. */
$purple: rgb(104, 48, 144);

.nd-modal {
  --nd-accent: #{$purple};
  --nd-soft: #f4effa;
  border-radius: 12px;
  overflow: hidden;
  border-top: 4px solid var(--nd-accent);

  &.nd-modal--warning {
    --nd-accent: #e6a23c;
    --nd-soft: #fdf6ec;
  }
  &.nd-modal--danger {
    --nd-accent: #f56c6c;
    --nd-soft: #fef0f0;
  }

  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    padding: 32px 36px 8px;
  }
  .el-dialog__footer {
    padding: 8px 24px 20px;
  }
}

.nd-modal__body {
  text-align: center;
}

.nd-modal__badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: var(--nd-soft);
}

.nd-modal__ring {
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  border: 1px dashed var(--nd-accent);
  opacity: 0.45;
}

.nd-modal__icon {
  font-size: 2em;
  color: var(--nd-accent);
}

.nd-modal__ring--spin {
  border-style: solid;
  border-color: var(--nd-accent) transparent var(--nd-accent) transparent;
  opacity: 0.8;
  animation: nd-spin 1.4s linear infinite;
}

.nd-modal__icon--pulse {
  animation: nd-pulse 1.4s ease-in-out infinite;
}

.nd-modal__progress {
  margin: 4px 0 8px;
}

.nd-modal__progress-text {
  margin: 0 0 20px;
  text-align: center;
  font-size: 0.85em;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: var(--nd-accent);
}

@keyframes nd-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes nd-pulse {
  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

.nd-modal__title {
  margin: 0 0 8px;
  font-size: 1.15em;
  font-weight: 600;
  color: #303133;
}

.nd-modal__text {
  margin: 0 0 14px;
  white-space: pre-line;
  color: #606266;
  line-height: 1.55;
}

.nd-modal__hint {
  display: inline-block;
  margin: 0;
  padding: 6px 12px;
  border-radius: 14px;
  background: #f7f8fa;
  font-size: 0.82em;
  color: #909399;

  .fa {
    margin-right: 4px;
    color: #e6a23c;
  }
}

.nd-modal__footer {
  display: flex;
  justify-content: center;
  gap: 8px;
}
</style>



// WEBPACK FOOTER //
// NoDataModal.vue?aaaf7e72